/**
 * @class SoulogStickySteps
 * Pinned step-by-step showcase (pure JS, no dependencies).
 *
 * Desktop (≥750px): pinned full-screen within the track; scroll progress decides the active step, left-column text highlights + the accent indicator bar follows,
 *   and the right-column panel media cross-fades; the entrance envelope (rotateY/blur/scale/offset + staggered list) advances as the track moves into the viewport, completing before pinning.
 *   Clicking a step → smooth-scrolls to that step (with scrollLock, to avoid fighting the scroll listener).
 * Mobile (<750px): no pinning, no scroll listener; units stack in a single column and fade in on entering the viewport.
 * prefers-reduced-motion: disables offset/blur/rotateY, content is visible immediately, jumps use auto.
 * No JS: [data-ready] isn't added, and CSS keeps all content permanently visible in normal document flow.
 * Shopify compatibility: scope limited to the section root, re-initialized in connectedCallback, cleaned up in disconnectedCallback.
 */
if (!customElements.get("soulog-sticky-steps")) {
	const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
	const smoothstep = (t) => t * t * (3 - 2 * t);

	// Entrance envelope span, in viewport heights. The entrance completes while the track's top edge rises from the viewport bottom (rect.top = vh) to
	// rect.top = vh × (1 - ENTER_SPAN); everything is fully shown before pinning (rect.top = 0).
	const ENTER_SPAN = 0.75;

	// Safari <14 uses addListener, newer versions use addEventListener
	const bindMq = (mq, fn) => {
		if (mq.addEventListener) mq.addEventListener("change", fn);
		else if (mq.addListener) mq.addListener(fn);
	};
	const unbindMq = (mq, fn) => {
		if (mq.removeEventListener) mq.removeEventListener("change", fn);
		else if (mq.removeListener) mq.removeListener(fn);
	};

	class SoulogStickySteps extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector(".sss__track");
			this.head = this.querySelector(".sss__head");
			// One accent vertical bar per group (each group has its own .sss__grid positioning context)
			this.indicators = Array.from(this.querySelectorAll(".sss__indicator"));
			this.pillsBar = this.querySelector(".sss__pills");
			this.pillInd = this.querySelector(".sss__pill-ind");
			this.steps = Array.from(this.querySelectorAll(".sss__step"));
			this.medias = Array.from(this.querySelectorAll(".sss__media"));
			this.N = this.steps.length;
			this.stepMetrics = null;

			// Group model. Pills and groups are paired by data-group rather than array index — the anonymous first segment
			// (the intermediate state where the merchant added steps before adding groups) has no pill, so pairing by index would shift everything by one.
			// With no group blocks this is exactly "one group, no pills", and every branch below degrades to the pre-grouping behavior.
			const pills = Array.from(this.querySelectorAll(".sss__pill"));
			this.groups = Array.from(this.querySelectorAll(".sss__group")).map((el, i) => ({
				el,
				i,
				start: Number(el.dataset.start) || 0,
				count: Number(el.dataset.count) || 0,
				indicator: el.querySelector(".sss__indicator"),
				pill: pills.find((p) => p.dataset.group === el.dataset.group) || null,
			}));
			this.groupIndex = -1;

			// The index used for the entrance stagger (applyEnvelope) must be the **in-group** index, not the cross-group global index.
			// The formula is li = smoothstep(clamp((envRaw - index × 0.13) / 0.62, 0, 1)), and
			// envRaw caps at 1 — with the global index, the steady-state opacity after the entrance completes would be
			// i=4 → 0.90, i=5 → 0.62, i=6 → 0.29, i=7 → 0.06, i≥8 → 0,
			// so the whole 3rd group looked like it hadn't loaded (measured 2026-08-06).
			// Before grouping each section had at most 3 steps and i was always ≤2, so this cap was never hit.
			this.stepLocal = this.steps.map((_, i) => {
				const g = this.groups[this.groupOf(i)];
				return g ? i - g.start : i;
			});

			// Calibrate the panel ratio with the video's own reported true aspect ratio (a pitfall measured live on 2026-08-05).
			//
			// Why the value injected by Liquid can't be trusted alone:
			//   1. Shopify's video.aspect_ratio is rounded (measured as 1.45, while the footage's
			//      2352×1620 true value is 1.451852);
			//   2. For external mp4s (video_url) Liquid can't read the metadata at all, relying on the merchant manually filling in "Media aspect ratio";
			//      if it's wrong or forgotten it won't match at all (live, a forgotten value once gave a container of 828×480, ratio 1.725,
			//      with the video at 1.4519, leaving a big empty strip on each side).
			//
			// Unless the container ratio and video ratio are exactly equal, object-fit: contain leaves empty strips, and
			// a <video>'s empty strips are painted **black** by the browser. When far off it's an obvious black border; when only slightly off it's
			// a half-physical-pixel black line, invisible when static but revealed when the two layers overlap during a cross-fade — this is exactly
			// the cause of the "black line at the top when switching".
			//
			// As soon as the metadata arrives, write back the exact ratio (and add the --ratio modifier class so the container really follows the ratio);
			// the container and video then fit perfectly, and the empty strip width is always 0.
			this.medias.forEach((media) => {
				const v = media.querySelector("video");
				if (!v) return;
				const apply = () => {
					if (!v.videoWidth || !v.videoHeight) return;
					media.style.setProperty("--sss-media-ratio", (v.videoWidth / v.videoHeight).toFixed(6));
					media.classList.add("sss__media--ratio");
					// Writing back the ratio changes the panel height → the grid row height changes → the left-column steps' offsetTop changes with it.
					// And the indicator bar's positioning basis, stepMetrics, is cached at setup(); without re-measuring it stays at the old position
					// (measured 2026-08-05: Insight module cached 171 / actual 201, the indicator bar was 30px too high).
					// Empty stepMetrics means setup() hasn't run yet (synchronous branch); it will measure on its own, so nothing to do here.
					if (this.stepMetrics && this.onResize) this.onResize();
				};
				// HAVE_METADATA = 1; if already ready write directly, otherwise wait for metadata
				if (v.readyState >= 1) apply();
				else v.addEventListener("loadedmetadata", apply, { once: true });
			});

			// split (captions overlaid on the video's empty area) groups: each group pairs one video with a column of captions.
			// Each caption has its own start/end window [at, until), and gaps are allowed — in the latter part of the footage picture content appears
			// on the right side of the video, and at that moment none should be lit.
			// cues must be sorted ascending by at — pickWindow's loop relies on ascending order to break early; out of order it would pick the wrong segment.
			// Only the array is sorted, not the DOM: in the single-caption switching form the item order isn't visible anyway.
			this.capGroups = Array.from(this.querySelectorAll(".sss__media--split"))
				.map((media) => {
					const list = media.querySelector(".sss__caps");
					const video = media.querySelector("video");
					if (!list || !video) return null;
					const cues = Array.from(list.querySelectorAll(".sss__cap"))
						.map((el) => ({
							at: parseFloat(el.dataset.at) || 0,
							// data-until missing / invalid → Infinity, degrading to "lit until the video ends" rather than "never lit"
							until: parseFloat(el.dataset.until) || Infinity,
							el,
						}))
						.sort((a, b) => a.at - b.at);
					return cues.length ? { list, video, cues, on: -1 } : null;
				})
				.filter(Boolean);

			this.desktopMq = window.matchMedia("(min-width: 750px)");
			this.reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

			this.index = -1;
			this.ticking = false;
			this.scrollLock = false;
			this.inView = false;
			this._desktop = false;
			this.io = null;

			this.onScroll = () => this.requestUpdate();
			this.onResize = () => {
				this.measure();
				this.requestUpdate();
			};
			this.onModeChange = () => this.setup();

			bindMq(this.desktopMq, this.onModeChange);
			bindMq(this.reduceMq, this.onModeChange);

			if (this.N > 0) this.setAttribute("data-ready", "");
			this.setup();
		}

		disconnectedCallback() {
			this.teardown();
			if (this.desktopMq) unbindMq(this.desktopMq, this.onModeChange);
			if (this.reduceMq) unbindMq(this.reduceMq, this.onModeChange);
		}

		setup() {
			this.teardown();
			// Mobile doesn't pin, so hand back the CSS defaults; the desktop branch rewrites them in measure()
			this.style.removeProperty("--sss-reserve");
			if (this.N === 0) return;
			if (this.desktopMq.matches) this.setupDesktop();
			else this.setupMobile();
			this.bindCaps();
		}

		/* ===================== Desktop ===================== */
		setupDesktop() {
			this._desktop = true;
			// Disable blur on low-end devices / reduced-motion (continuously repainting blur on a large panel is expensive)
			this.allowBlur =
				!this.reduceMq.matches && (navigator.hardwareConcurrency === undefined || navigator.hardwareConcurrency > 4);

			this.stepClick = this.steps.map((s) => {
				const h = () => this.goToStep(Number(s.dataset.idx));
				s.addEventListener("click", h);
				return h;
			});

			// Clicking a pill = jump to that group's first step. Reuses goToStep's scrollLock, so that during the smooth scroll
			// the scroll listener doesn't change the active item back based on progress.
			this.pillClick = this.groups.map((g) => {
				if (!g.pill) return null;
				const h = () => this.goToStep(g.start);
				g.pill.addEventListener("click", h);
				return h;
			});

			this.io = new IntersectionObserver(
				(entries) => {
					this.inView = entries.some((en) => en.isIntersecting);
					if (this.inView) {
						// Symmetrical with the else branch's pauseAll(): requestUpdate() is an async rAF,
						// and calling only that, playActive() would use a stale index not yet corrected by update()
						// (when scroll restoration / an anchor deep link lands mid-track, step 0 would play and immediately pause).
						// So run update() synchronously once here to correct the index, then start playback.
						this.update();
						this.playActive();
					} else {
						this.pauseAll();
					}
				},
				{ rootMargin: "20% 0px" },
			);
			this.io.observe(this.track);

			window.addEventListener("scroll", this.onScroll, { passive: true });
			window.addEventListener("resize", this.onResize);

			this.measure();
			this.setIndex(0);
			this.update();

			// The webfont swap often happens after this measure() (defer scripts usually run before the body font swaps in);
			// after switching fonts the left-column steps rewrap, and the stepMetrics cached by measure() immediately go stale —
			// the indicator bar would stay at the old position. Re-run onResize (measure + repaint) once fonts are ready as a fallback.
			// Safe no-op when already disconnected: all sizes measure() reads are 0, and the next setup() will re-measure.
			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(() => {
					if (this._desktop && this.onResize) this.onResize();
				});
			}
		}

		/* ===================== Mobile ===================== */
		setupMobile() {
			this._desktop = false;
			this.io = new IntersectionObserver(
				(entries) => {
					for (const en of entries) {
						const v = en.target.querySelector ? en.target.querySelector("video") : null;
						if (en.isIntersecting) {
							en.target.classList.add("is-in");
							if (v) {
								const r = v.play();
								if (r && r.catch) r.catch(() => {});
							}
						} else if (v) {
							v.pause();
						}
					}
				},
				{ threshold: 0.35 },
			);
			this.steps.forEach((s) => this.io.observe(s));
			this.medias.forEach((m) => this.io.observe(m));
		}

		/* ===================== Scroll driving ===================== */
		requestUpdate() {
			if (this.ticking) return;
			this.ticking = true;
			requestAnimationFrame(() => {
				this.ticking = false;
				this.update();
			});
		}

		update() {
			if (!this._desktop || !this.inView || this.N === 0 || !this.track) return;
			const vh = window.innerHeight;
			const rect = this.track.getBoundingClientRect();
			const total = rect.height - vh;
			const p = total > 0 ? clamp(-rect.top / total, 0, 0.9999) : 0;

			// Active step: progress split evenly across N steps
			if (!this.scrollLock) this.setIndex(clamp(Math.floor(p * this.N), 0, this.N - 1));

			// Entrance envelope: advances as the track moves into the viewport, completing before pinning.
			// It can't be tied to the pin progress p — p = -rect.top/total is clamped to [0, ..], so during the entire move-in phase
			// (rect.top dropping from vh to 0, about 100vh of scrolling) it stays 0, which would keep a section that already fills the screen at opacity:0 throughout.
			const envRaw = clamp((vh - rect.top) / (vh * ENTER_SPAN), 0, 1);
			const e = smoothstep(envRaw);
			this.applyEnvelope(e, envRaw);
			this.moveIndicator(e);
		}

		setIndex(i) {
			i = clamp(i, 0, this.N - 1);
			if (i === this.index) return;
			this.index = i;
			this.steps.forEach((s, k) => {
				s.classList.toggle("is-on", k === i);
				s.setAttribute("aria-current", k === i ? "true" : "false");
			});
			this.medias.forEach((m, k) => {
				m.classList.toggle("is-on", k === i);
				if (this.reduceMq.matches) return;
				if (k === i) {
					// The active media's opacity/transform/filter are written uniformly by applyEnvelope (entrance envelope), to avoid two places fighting
					return;
				}
				m.style.opacity = "";
				m.style.transform = "translateY(" + (k < i ? -44 : 44) + "px) scale(0.94)";
				m.style.filter = this.allowBlur ? "blur(7px)" : "none";
			});
			this.setGroup(this.groupOf(i));
			this.playActive();
		}

		/** Global step index → group number. Groups are arranged ascending by start; search backwards for the first with start ≤ i. */
		groupOf(i) {
			for (let k = this.groups.length - 1; k >= 0; k--) {
				if (i >= this.groups[k].start) return k;
			}
			return 0;
		}

		setGroup(g) {
			if (g === this.groupIndex) return;
			this.groupIndex = g;
			this.groups.forEach((grp) => {
				const on = grp.i === g;
				grp.el.classList.toggle("is-on", on);
				// Those already slid out stay on the left, those waiting to slide in wait on the right — same direction as the pill indicator
				grp.el.style.setProperty("--sss-group-x", on ? "0px" : grp.i < g ? "-100%" : "100%");
				grp.el.setAttribute("aria-hidden", on ? "false" : "true");
				if (grp.pill) {
					grp.pill.classList.toggle("is-on", on);
					grp.pill.setAttribute("aria-selected", on ? "true" : "false");
				}
			});
			this.movePillIndicator();
		}

		/** The indicator follows the current pill. offsetLeft is relative to .sss__pills (which is position:relative). */
		movePillIndicator() {
			const g = this.groups[this.groupIndex];
			if (!this.pillInd || !g || !g.pill) return;
			this.pillInd.style.width = g.pill.offsetWidth + "px";
			this.pillInd.style.transform = "translateX(" + g.pill.offsetLeft + "px)";
			this.pillInd.style.opacity = "1";
		}

		measure() {
			this.stepMetrics = this.steps.map((s) => ({ top: s.offsetTop, height: s.offsetHeight }));
			this.syncReserve();
			// Pill text widths change after the webfont swaps in; measure() is already hooked to both resize and
			// document.fonts.ready, so just piggyback on it to re-measure here.
			this.movePillIndicator();
		}

		/**
		 * Height to reserve in the pinned area besides the panel (heading block + top/bottom breathing room), written back to --sss-reserve so CSS can clamp the panel width.
		 * Hard-coding 260px over-reserves on short viewports (laptop with DevTools open, ~570px visible height),
		 * squeezing the panel small and off-center with a big empty area on the right; computing from the heading's measured height avoids wasting space.
		 */
		syncReserve() {
			const breathe = 64;
			let reserve = breathe;
			if (this.head) {
				const cs = getComputedStyle(this.head);
				reserve += this.head.offsetHeight + (parseFloat(cs.marginBottom) || 0);
			}
			// The pill bar is a new vertical block in the pinned area; leaving it out would make the panel exceed 100vh (especially on short viewports).
			// On mobile / without JS it's display:none, so offsetHeight is naturally 0 and needs no special case.
			if (this.pillsBar) {
				const cs = getComputedStyle(this.pillsBar);
				reserve += this.pillsBar.offsetHeight + (parseFloat(cs.marginBottom) || 0);
			}
			this.style.setProperty("--sss-reserve", Math.round(reserve) + "px");
		}

		moveIndicator(e) {
			const metrics = this.stepMetrics && this.stepMetrics[this.index];
			const grp = this.groups[this.groupIndex];
			const ind = grp && grp.indicator;
			// The offsetTop values in stepMetrics are relative to each .sss__grid (which is position:relative),
			// so after grouping they're naturally in-group relative values, and the algorithm needs no change.
			if (!metrics || !ind) return;
			ind.style.top = metrics.top + 14 + "px";
			ind.style.height = Math.max(0, metrics.height - 28) + "px";
			ind.style.opacity = this.reduceMq.matches ? 1 : e;
		}

		applyEnvelope(e, envRaw) {
			// reduced-motion: write no transforms, content is visible immediately
			if (this.reduceMq.matches) return;

			if (this.head) {
				this.head.style.opacity = e;
				this.head.style.transform = "translateY(" + ((1 - e) * 26).toFixed(1) + "px)";
			}

			// List items slide in staggered. The index is the in-group position (see the stepLocal comment in connectedCallback) —
			// using the cross-group global index would keep steps from the 3rd group onward from ever reaching opacity 1.
			this.steps.forEach((s, i) => {
				const li = smoothstep(clamp((envRaw - (this.stepLocal[i] || 0) * 0.13) / 0.62, 0, 1));
				s.style.opacity = li;
				s.style.transform = "translateX(" + ((1 - li) * -28).toFixed(1) + "px)";
			});

			// Whole-panel entrance (applied to the active media; non-active media offsets are handled by setIndex)
			const panelTf =
				"perspective(1200px) rotateY(" +
				((1 - e) * -11).toFixed(2) +
				"deg) translateX(" +
				((1 - e) * 54).toFixed(1) +
				"px) scale(" +
				(0.9 + e * 0.1).toFixed(4) +
				")";
			// After the entrance completes (1-e)*9 goes to zero, but writing "blur(0px)" is still a filter — the element would
			// stay on the offscreen rendering path, leaking black at the edges while scrolling (see the background color comment on .sss__media). Once it's zero,
			// remove the filter entirely, which both drops that path and saves a layer of offscreen compositing.
			const panelBlurPx = this.allowBlur ? (1 - e) * 9 : 0;
			const panelBlur = panelBlurPx >= 0.05 ? "blur(" + panelBlurPx.toFixed(1) + "px)" : "none";
			this.medias.forEach((m, k) => {
				if (k !== this.index) return;
				m.style.opacity = e;
				m.style.transform = panelTf;
				m.style.filter = panelBlur;
				// While the entrance is incomplete (envRaw < 1), disable the CSS transition to avoid fighting the per-frame scrub and turning into
				// a lagging rubber-band feel; restore the transition once e≥1, for the cross-fade's .is-on to use.
				m.classList.toggle("is-scrub", envRaw < 1);
			});
		}

		goToStep(i) {
			if (!this._desktop || this.N === 0 || !this.track) return;
			i = clamp(i, 0, this.N - 1);
			const vh = window.innerHeight;
			const rect = this.track.getBoundingClientRect();
			const absTop = rect.top + window.scrollY;
			const total = rect.height - vh;
			if (total <= 0) return;
			// Land at the midpoint of that step's range, to avoid bouncing back and forth at a boundary
			const target = absTop + ((i + 0.5) / this.N) * total;

			// Lock scroll driving, otherwise during the smooth scroll update() would change the active item back based on progress, fighting the jump
			this.scrollLock = true;
			this.setIndex(i);
			window.scrollTo({ top: target, behavior: this.reduceMq.matches ? "auto" : "smooth" });
			clearTimeout(this._lockTimer);
			this._lockTimer = setTimeout(() => {
				this.scrollLock = false;
			}, 700);
		}

		playActive() {
			this.medias.forEach((m, k) => {
				const v = m.querySelector("video");
				if (!v) return;
				if (k === this.index && this._desktop && this.inView) {
					const r = v.play();
					if (r && r.catch) r.catch(() => {});
				} else {
					v.pause();
				}
			});
		}

		pauseAll() {
			this.medias.forEach((m) => {
				const v = m.querySelector("video");
				if (v) v.pause();
			});
		}

		/* ===================== Timeline captions (split) ===================== */

		/**
		 * Everything is a pure function of currentTime: the video looping around, being paused by IntersectionObserver,
		 * or failing to autoplay on mobile — none of these three cases need special handling; wherever time goes, the display goes to that caption.
		 *
		 * i === -1 is a **normal state**, not an error: each caption has its own start/end window, and when time falls in a gap
		 * (or after the last one ends) none should be lit — in the latter part of the footage picture content appears on the right side of the video,
		 * and the captions must make way (2026-08-05 requirement). So there's no "fall back to caption 0" here.
		 *
		 * The exception is when SoulogVideoCues is missing entirely (a deploy missed the file / CDN 404): teardown() clears
		 * the SSR first caption's is-on (disconnectedCallback, and the start of setup(), both call teardown first),
		 * which is then restored via bindCaps() → syncCaps(g). If we returned directly here, that restore step would be skipped,
		 * showing up as the captions being entirely blank — worse than doing nothing. When missing, fall back to caption 0, equivalent to
		 * "degrade to no switching, but content always visible".
		 */
		syncCaps(g) {
			if (!g.cues.length) return;
			const api = globalThis.SoulogVideoCues;
			const i = api ? api.pickWindow(g.cues, g.video.currentTime || 0) : 0;
			if (i === g.on) return;
			g.on = i;
			// When i === -1, k === i is always false, so all items go dark together
			g.cues.forEach((c, k) => c.el.classList.toggle("is-on", k === i));
		}

		bindCaps() {
			if (!this.capGroups || !this.capGroups.length) return;
			this.capBinds = this.capGroups.map((g) => {
				const h = () => this.syncCaps(g);
				g.video.addEventListener("timeupdate", h);
				g.video.addEventListener("play", h);
				// seeked: correct immediately after the theme editor re-mounts / manual scrubbing, without waiting for the next timeupdate
				g.video.addEventListener("seeked", h);
				// The video may never play (power-saving mode), so set the first caption once at t=0
				this.syncCaps(g);
				return h;
			});
			// There used to be a height-reservation mechanism here that "measured the tallest caption and wrote it back to --sss-cap-min-h"
			// (plus a mobile-only resize listener + one document.fonts.ready re-measure).
			// All removed on 2026-08-07: after CSS switched to grid stacking the items still take part in layout, so the container height naturally
			// equals the tallest item, and font swaps / screen rotation are recomputed by the browser itself, with no cached value to go stale.
			// That mechanism was broken on mobile to begin with (the measured value was always 0, see the .sss__caps comment in the CSS);
			// don't add it back in any form.
			//
			// The desktop document.fonts.ready re-measure moved to setupDesktop(): a font swap makes the left-column
			// steps rewrap and stepMetrics go stale; that's unrelated to captions and shouldn't live in bindCaps().
		}

		/* ===================== Cleanup ===================== */
		teardown() {
			window.removeEventListener("scroll", this.onScroll);
			window.removeEventListener("resize", this.onResize);
			if (this.io) {
				this.io.disconnect();
				this.io = null;
			}
			clearTimeout(this._lockTimer);
			if (this.stepClick) {
				this.steps.forEach((s, i) => s.removeEventListener("click", this.stepClick[i]));
				this.stepClick = null;
			}
			if (this.pillClick && this.groups) {
				this.groups.forEach((g, i) => {
					if (g.pill && this.pillClick[i]) g.pill.removeEventListener("click", this.pillClick[i]);
				});
				this.pillClick = null;
			}
			if (this.capBinds && this.capGroups) {
				this.capGroups.forEach((g, i) => {
					g.video.removeEventListener("timeupdate", this.capBinds[i]);
					g.video.removeEventListener("play", this.capBinds[i]);
					g.video.removeEventListener("seeked", this.capBinds[i]);
					g.on = -1;
				});
				this.capBinds = null;
			}
			if (this.capGroups) {
				// is-on must be cleared, so the next connectedCallback isn't left a stale active state (harmless today —
				// after re-mounting, bindCaps()'s first syncCaps(g) corrects it immediately, but it's the same discipline as the inline style
				// cleanup of steps/medias below: cleanup must be thorough).
				this.capGroups.forEach((g) => {
					g.cues.forEach((c) => c.el.classList.remove("is-on"));
				});
			}
			this.pauseAll();
			this._desktop = false;
			this.inView = false;
			this.scrollLock = false;
			this.index = -1;
			this.stepMetrics = null;
			// Clean up inline styles thoroughly — no leftover artifacts when the theme editor re-mounts
			this.steps.forEach((s) => {
				s.classList.remove("is-on", "is-in");
				s.style.opacity = "";
				s.style.transform = "";
				s.removeAttribute("aria-current");
			});
			this.medias.forEach((m) => {
				m.classList.remove("is-on", "is-in", "is-scrub");
				m.style.opacity = "";
				m.style.transform = "";
				m.style.filter = "";
			});
			if (this.indicators) {
				this.indicators.forEach((ind) => {
					ind.style.top = "";
					ind.style.height = "";
					ind.style.opacity = "";
				});
			}
			if (this.head) {
				this.head.style.opacity = "";
				this.head.style.transform = "";
			}
			// Same discipline as above: no stale active group / pill state left behind when the theme editor re-mounts
			this.groupIndex = -1;
			if (this.groups) {
				this.groups.forEach((g) => {
					g.el.classList.remove("is-on");
					g.el.style.removeProperty("--sss-group-x");
					g.el.removeAttribute("aria-hidden");
					if (g.pill) {
						g.pill.classList.remove("is-on");
						g.pill.setAttribute("aria-selected", "false");
					}
				});
			}
			if (this.pillInd) {
				this.pillInd.style.width = "";
				this.pillInd.style.transform = "";
				this.pillInd.style.opacity = "";
			}
		}
	}

	customElements.define("soulog-sticky-steps", SoulogStickySteps);
}
